if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      connectedCallback() {
        this.select = this.querySelector('select[name="id"]');
        this.addButton = this.querySelector('[data-product-add]');
        this.addLabel = this.querySelector('[data-product-add-label]');
        this.addView = this.querySelector('[data-product-add-view]');
        this.inCartLink = this.querySelector('[data-product-in-cart]');
        this.inCartLabel = this.querySelector('[data-product-in-cart-label]');
        this.price = this.querySelector('[data-product-price]');
        this.comparePrice = this.querySelector('[data-product-compare-price]');
        this.unitPrice = this.querySelector('[data-product-unit-price]');
        this.availability = this.querySelector('[data-product-availability]');
        this.oneOnly = this.querySelector('[data-product-one]');
        this.quantity = this.querySelector('[data-product-quantity]');
        this.quantityLabel = this.querySelector('[data-product-quantity-label]');
        this.hiddenId = this.querySelector('[data-product-id]');
        this.mediaFrame = this.querySelector('[data-main-media-frame]');
        this.productRoot = this.querySelector('[data-product-root]');
        this.soldStamp = this.querySelector('[data-product-sold-stamp]');
        this.dynamicCheckout = this.querySelector('[data-product-dynamic-checkout]');
        this.form = this.querySelector('[data-product-form]');
        this.formFeedback = this.querySelector('[data-product-form-feedback]');
        this.formStatus = this.querySelector('[data-product-form-status]');
        this.cartRecovery = this.querySelector('[data-product-cart-recovery]');
        this.messageLinks = [...this.querySelectorAll('[data-product-message]')];

        this.variants = this.readJSON('[data-product-variants]', []);
        this.copy = this.readJSON('[data-product-copy]', {});
        this.defaultQuantityLabel = this.copy.quantity || this.quantityLabel?.textContent;

        this.onChange = () => this.updateVariant();
        this.onSubmit = (event) => this.submitAdd(event);
        this.onMessageClick = (event) => this.copyProductMessage(event.currentTarget);
        this.onThumbnailClick = (event) => {
          const thumbnail = event.target.closest('[data-product-thumb]');
          if (!thumbnail || !this.contains(thumbnail)) return;
          event.preventDefault();
          this.activateMedia(thumbnail.dataset.mediaId);
        };

        this.select?.addEventListener('change', this.onChange);
        this.form?.addEventListener('submit', this.onSubmit);
        this.messageLinks.forEach((link) => link.addEventListener('click', this.onMessageClick));
        this.addEventListener('click', this.onThumbnailClick);
        if (this.select && this.variants.length) this.dataset.variantEnhanced = '';
        if (this.form && this.formFeedback && this.dataset.cartAddUrl) {
          this.formFeedback.hidden = false;
          this.dataset.cartEnhanced = '';
        }
        const selected = this.selectedVariant();
        if (selected) this.renderCartQuantity(selected.cartQuantity);
      }

      disconnectedCallback() {
        this.select?.removeEventListener('change', this.onChange);
        this.form?.removeEventListener('submit', this.onSubmit);
        this.messageLinks?.forEach((link) => link.removeEventListener('click', this.onMessageClick));
        this.removeEventListener('click', this.onThumbnailClick);
        this.addRequest?.abort();
        window.clearTimeout(this.addResetTimer);
      }

      readJSON(selector, fallback) {
        try {
          return JSON.parse(this.querySelector(selector)?.textContent || '');
        } catch {
          return fallback;
        }
      }

      updateVariant() {
        const variant = this.variants.find((item) => String(item.id) === this.select?.value);
        if (!variant) return;
        this.clearAddSuccess();
        this.clearFormStatus();

        if (this.price) this.price.textContent = variant.price;
        if (this.comparePrice) {
          this.comparePrice.textContent = variant.compareAtPrice;
          this.comparePrice.hidden = !variant.onSale;
        }
        if (this.unitPrice) {
          this.unitPrice.textContent = variant.unitPrice || '';
          this.unitPrice.hidden = !variant.unitPrice;
        }

        if (this.addButton) {
          this.addButton.disabled = !variant.available;
          this.addButton.setAttribute('aria-disabled', String(!variant.available));
        }
        if (this.addLabel) this.addLabel.textContent = variant.available ? this.copy.add : this.copy.sold;
        if (this.availability) {
          // innerHTML rather than textContent: the availability copy carries the
          // shop address as a link. Both strings come from theme settings
          // rendered server-side, so this is the same trust level as the markup
          // this replaces — never customer input.
          this.availability.innerHTML = variant.available ? this.copy.available : this.copy.unavailable;
        }
        if (this.oneOnly) this.oneOnly.hidden = !variant.available;
        if (this.soldStamp) this.soldStamp.hidden = variant.available;
        if (this.dynamicCheckout) this.dynamicCheckout.hidden = !variant.available;
        this.productRoot?.classList.toggle('is-sold', !variant.available);
        if (this.hiddenId) this.hiddenId.value = variant.id;
        if (this.quantity && variant.quantityRule) {
          const { min = 1, max, increment = 1 } = variant.quantityRule;
          const fixedMax = Number(this.quantity.dataset.productQuantityLimit) || null;
          const effectiveMax = fixedMax ?? max;
          this.quantity.min = min;
          this.quantity.step = increment;
          if (effectiveMax == null) this.quantity.removeAttribute('max');
          else this.quantity.max = effectiveMax;
          this.quantity.value = Math.max(min, Math.min(Number(this.quantity.value) || min, effectiveMax ?? Infinity));
        }
        this.renderCartQuantity(variant.cartQuantity);

        if (variant.featuredMediaId) this.activateMedia(String(variant.featuredMediaId));

        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
      }

      async submitAdd(event) {
        if (!this.form || !this.dataset.cartAddUrl) return;
        if (event.submitter && event.submitter !== this.addButton) return;
        event.preventDefault();
        if (this.addPending) return;
        const restoreFocus = document.activeElement === this.addButton;
        this.clearAddSuccess();
        this.addPending = true;
        this.addRequest = new AbortController();
        this.clearFormStatus();
        if (this.addButton) {
          this.addButton.disabled = true;
          this.addButton.setAttribute('aria-busy', 'true');
        }
        if (this.addLabel) this.addLabel.textContent = this.copy.adding || this.copy.add;

        let added = false;
        try {
          const response = await fetch(this.dataset.cartAddUrl, {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: new FormData(this.form),
            signal: this.addRequest.signal,
          });
          const payload = await this.readResponse(response);
          if (!response.ok) {
            throw new Error(this.responseMessage(payload));
          }
          added = true;
          const selected = this.selectedVariant();
          const addedQuantity = Number(this.quantity?.value) || 1;
          if (selected) selected.cartQuantity = (Number(selected.cartQuantity) || 0) + addedQuantity;
          this.showAddSuccess(selected?.cartQuantity || addedQuantity);
          this.showFormStatus(this.copy.added || this.copy.add, { showCart: true });
          const badge = document.querySelector('[data-cart-count]');
          this.syncHeader({ item_count: (Number(badge?.textContent) || 0) + 1 }, true);
          try {
            this.syncCart(await this.readCart(), true);
          } catch {}
        } catch (error) {
          if (error.name === 'AbortError') return;
          try {
            this.syncCart(await this.readCart());
          } catch {}
          this.showFormStatus(error.message || this.copy.addError || '', {
            error: true,
            showCart: true,
          });
        } finally {
          this.addRequest = null;
          this.addPending = false;
          let unavailable = false;
          if (this.addButton) {
            const selected = this.variants.find((item) => String(item.id) === this.select?.value);
            unavailable = selected ? !selected.available : false;
            this.addButton.disabled = unavailable;
            this.addButton.setAttribute('aria-disabled', String(unavailable));
            this.addButton.removeAttribute('aria-busy');
          }
          if (this.addLabel && !added) this.addLabel.textContent = unavailable ? this.copy.sold : this.copy.add;
          if (restoreFocus && [document.body, this.addButton].includes(document.activeElement)) {
            (this.inCartLink && !this.inCartLink.hidden ? this.inCartLink : this.addButton)?.focus({ preventScroll: true });
          }
        }
      }

      showAddSuccess(quantity) {
        window.clearTimeout(this.addResetTimer);
        this.renderCartQuantity(quantity);
        if (this.inCartLink) this.inCartLink.dataset.state = 'just-added';
        if (this.inCartLabel) this.inCartLabel.textContent = this.copy.addedShort || this.copy.added || this.copy.add;
        this.addResetTimer = window.setTimeout(() => this.clearAddSuccess(), 1800);
      }

      clearAddSuccess() {
        window.clearTimeout(this.addResetTimer);
        if (this.inCartLink) delete this.inCartLink.dataset.state;
        if (this.addLabel && this.copy) this.addLabel.textContent = this.copy.add || this.addLabel.textContent;
        this.renderCartQuantity(this.selectedVariant()?.cartQuantity);
      }

      selectedVariant() {
        const id = this.select?.value || this.hiddenId?.value;
        return this.variants.find((item) => String(item.id) === String(id)) || this.variants[0];
      }

      renderCartQuantity(quantity, preserveCelebration = false) {
        const count = Math.max(0, Number(quantity) || 0);
        const previousCount = this.cartQuantity;
        const cartLinkFocused = !!this.inCartLink && document.activeElement === this.inCartLink;
        this.cartQuantity = count;
        if (this.addView) this.addView.hidden = count > 0;
        if (this.inCartLink) this.inCartLink.hidden = count === 0;
        if (this.quantity) {
          this.quantity.disabled = count > 0;
          if (count > 0) this.quantity.value = count;
          else if (previousCount > 0) this.quantity.value = this.quantity.min || 1;
        }
        if (cartLinkFocused && count === 0) this.addButton?.focus({ preventScroll: true });
        if (this.quantityLabel) {
          this.quantityLabel.textContent = count > 0 ? this.copy.quantityInCart : this.defaultQuantityLabel;
        }
        if (preserveCelebration && this.inCartLink?.dataset.state === 'just-added') return;
        if (this.inCartLink) delete this.inCartLink.dataset.state;
        if (this.inCartLabel) {
          this.inCartLabel.textContent = count > 1
            ? (this.copy.inCartCount || '__COUNT__ in cart').replace('__COUNT__', String(count))
            : this.copy.inCart || 'In cart';
        }
      }

      async readResponse(response) {
        const text = await response.text();
        if (!text) return {};
        try {
          return JSON.parse(text);
        } catch {
          // Proxy/challenge HTML is not shopper-facing error copy.
          return {};
        }
      }

      responseMessage(payload) {
        const message = payload?.description || payload?.message;
        return typeof message === 'string' && message.trim()
          ? message.trim()
          : this.copy.addError || '';
      }

      async readCart() {
        const response = await fetch(`${this.dataset.cartUrl}.js`, {
          headers: { Accept: 'application/json' },
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.json();
      }

      syncCart(cart, preserveCelebration = false) {
        if (Array.isArray(cart?.items)) {
          this.variants.forEach((variant) => {
            variant.cartQuantity = cart.items.reduce((total, item) => {
              const id = item.variant_id ?? item.variant?.id;
              return String(id) === String(variant.id) ? total + (Number(item.quantity) || 0) : total;
            }, 0);
          });
          this.renderCartQuantity(this.selectedVariant()?.cartQuantity, preserveCelebration);
        }
        this.syncHeader(cart);
      }

      syncHeader(cart, celebrate = false) {
        const count = Number(cart?.item_count) || 0;
        document.querySelectorAll('[data-cart-link]').forEach((link) => {
          const badge = link.querySelector('[data-cart-count]');
          if (badge) {
            badge.textContent = String(count);
            badge.hidden = count === 0;
          }
          if (count === 0) link.setAttribute('aria-label', link.dataset.cartEmptyLabel || '');
          else if (count === 1) link.setAttribute('aria-label', link.dataset.cartOneLabel || '');
          else {
            link.setAttribute(
              'aria-label',
              (link.dataset.cartOtherLabel || '').replace('__COUNT__', String(count))
            );
          }

          if (celebrate) {
            link.dataset.state = 'just-added';
            window.setTimeout(() => delete link.dataset.state, 650);
          }
        });
      }

      async copyProductMessage(link) {
        const text = link?.dataset.copyText?.trim();
        if (!text) return;

        let copied = false;
        try {
          await navigator.clipboard.writeText(text);
          copied = true;
        } catch {}

        const status = link.closest('.product__question-wrap')?.querySelector('[data-product-message-status]');
        if (status) status.textContent = copied ? this.dataset.copySuccess || '' : this.dataset.copyFailure || '';
      }

      clearFormStatus() {
        if (this.formFeedback) delete this.formFeedback.dataset.state;
        if (this.formStatus) this.formStatus.textContent = '';
        if (this.cartRecovery) this.cartRecovery.hidden = true;
      }

      showFormStatus(message, { error = false, showCart = false } = {}) {
        if (this.formFeedback) this.formFeedback.dataset.state = error ? 'error' : 'success';
        if (this.formStatus) this.formStatus.textContent = message;
        if (this.cartRecovery) this.cartRecovery.hidden = !showCart;
      }

      activateMedia(mediaId) {
        const thumbnail = this.querySelector(`[data-product-thumb][data-media-id="${CSS.escape(mediaId)}"]`);
        const media = this.querySelector(`[data-product-media-item][data-media-id="${CSS.escape(mediaId)}"]`);
        if (!thumbnail || !media || !this.mediaFrame) return;

        this.querySelectorAll('[data-product-media-item]').forEach((item) => {
          item.hidden = item !== media;
          if (item === media) return;

          item.querySelector('video')?.pause();
          item.querySelector('model-viewer')?.pause?.();
          const frame = item.querySelector('iframe');
          if (frame?.src && frame.src !== 'about:blank') {
            frame.dataset.mediaSrc = frame.src;
            frame.src = 'about:blank';
          }
        });

        const deferred = media.querySelector('template[data-deferred-media]');
        if (deferred) deferred.replaceWith(deferred.content.cloneNode(true));
        const suspendedFrame = media.querySelector('iframe[data-media-src]');
        if (suspendedFrame) {
          suspendedFrame.src = suspendedFrame.dataset.mediaSrc;
          delete suspendedFrame.dataset.mediaSrc;
        }
        this.mediaFrame.dataset.currentMediaId = mediaId;

        this.querySelectorAll('[data-product-thumb]').forEach((item) => {
          const active = item === thumbnail;
          item.classList.toggle('is-active', active);
          if (active) item.setAttribute('aria-current', 'true');
          else item.removeAttribute('aria-current');
        });
      }
    }
  );
}
